import React, { useState, useEffect } from 'react';
import '../styles/Orb.css';
import { generateCourse, generateCourseInitial, continueGenerationInBackground, getUserGenerationStatus } from '../context/engine.ts';
import { classifyTopic } from '../context/topic-classifier.ts';
import { validateInput, getValidationErrorMessage, sanitizeForPrompt } from '../utils/inputValidation.js';
import { CgSpinner } from 'react-icons/cg';

function AskInput({ isAuthenticated, currentUser, onCourseGenerated }) {
    const [inputValue, setInputValue] = useState('');
    const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [generationStatus, setGenerationStatus] = useState('');
    const [usageStatus, setUsageStatus] = useState(null);
    const [validationWarnings, setValidationWarnings] = useState([]);
    const [validationStatus, setValidationStatus] = useState(null);

    // List of subjects to cycle through in the placeholder
    const placeholderSubjects = [
        "Learn almost anything...",
        "Mathematics",
        "Physics",
        "Chemistry",
        "Biology",
        "Economics",
        "Psychology",
        "History",
        "Literature",
        "Philosophy",
        "Medicine",
        "Nutrition",
        "Finance",
        "Statistics",
        "Astronomy",
        "Geography",
        "React Frontend",
        "JavaScript Programming",
        "Python Programming",
        "Machine Learning",
        "Data Science",
        "Calculus",
        "Organic Chemistry",
        "World History",
        "Business Strategy"
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setIsTransitioning(true);

            setTimeout(() => {
                setCurrentPlaceholderIndex((prevIndex) =>
                    (prevIndex + 1) % placeholderSubjects.length
                );
                setIsTransitioning(false);
            }, 300); // Wait for fade-out before changing text

        }, 3000); // Change placeholder every 3 seconds

        return () => clearInterval(interval);
    }, [placeholderSubjects.length]);

    // Fetch usage status when user changes or component mounts
    useEffect(() => {
        const fetchUsageStatus = async () => {
            if (currentUser && currentUser.id && currentUser.subscription_type === 'premium') {
                try {
                    const status = await getUserGenerationStatus(currentUser.id);
                    setUsageStatus(status);
                } catch (err) {
                }
            }
        };

        fetchUsageStatus();
    }, [currentUser]);

    // Handle input change with real-time validation
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Clear previous errors when user starts typing
        if (error) {
            setError(null);
        }

        // Only validate if there's actual input
        if (newValue.trim().length > 0) {
            const validation = validateInput(newValue);

            // Show warnings but don't block input
            setValidationWarnings(validation.warnings);
            setValidationStatus(validation.severity);

            // Show errors for character validation in real-time
            if (!validation.isValid && validation.errors.length > 0) {
                // Only show character errors in real-time, not length errors
                // (to avoid annoying users while typing)
                const charErrors = validation.errors.filter(err =>
                    err.includes('Invalid characters') ||
                    err.includes('not allowed')
                );
                if (charErrors.length > 0) {
                    setError(charErrors[0]);
                }
            }
        } else {
            // Clear validation warnings when input is empty
            setValidationWarnings([]);
            setValidationStatus(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isAuthenticated || !currentUser) {
            setError('Please sign in to generate a course');
            return;
        }

        // Check if user has premium subscription
        if (currentUser.subscription_type !== 'premium') {
            setError('Upgrade to Premium to generate custom courses!');
            return;
        }

        // Comprehensive validation check
        const validation = validateInput(inputValue);

        if (!validation.isValid) {
            setError(getValidationErrorMessage(validation));
            return;
        }

        // If flagged for review but not blocked, proceed but log it
        if (validation.severity === 'flag') {
            console.warn('Content flagged for review:', {
                userId: currentUser.id,
                input: inputValue.trim(),
                timestamp: new Date().toISOString()
            });
            // TODO: Log to audit table when implemented
        }

        setIsGenerating(true);
        setError(null);
        setValidationWarnings([]);
        setGenerationStatus('Analyzing topic...');

        try {
            // Sanitize input before sending to AI
            const sanitizedInput = sanitizeForPrompt(inputValue);

            // Step 1: Classify the topic
            const classification = await classifyTopic(sanitizedInput);

            setGenerationStatus(
                classification.requiresRealtime
                    ? 'Generating course with real-time data...'
                    : 'Generating course...'
            );

            // Step 2: Generate course initial (fast) with classification and user settings
            const isPremium = currentUser?.subscription_type === 'premium';
            const userSettings = {
                vocabularyLevel: currentUser?.vocabulary_level || currentUser?.vocabularyLevel || 'auto',
                courseLength: currentUser?.course_length || currentUser?.courseLength || 0,
                isPremium: isPremium
            };
            const course = await generateCourseInitial(currentUser.id, sanitizedInput, classification, userSettings);

            // Notify parent component about the new course - navigate immediately!
            if (onCourseGenerated) {
                onCourseGenerated(course);
            }

            // Start background generation (don't await - fire and forget)
            continueGenerationInBackground(course.id, currentUser.id, sanitizedInput, classification, userSettings, course.fullOutline)
                .catch(err => console.error('Background generation failed:', err));

            // Refresh usage status after successful generation
            try {
                const updatedStatus = await getUserGenerationStatus(currentUser.id);
                setUsageStatus(updatedStatus);
            } catch (statusErr) {
            }

            // Clear the input and status
            setInputValue('');
            setGenerationStatus('');
        } catch (err) {

            // Special handling for generation limit errors
            if (err.name === 'GENERATION_LIMIT_REACHED') {
                setError(err.message);
            } else {
                setError(err.message || 'Failed to generate course. Please try again.');
            }

            setGenerationStatus('');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {/* Status messages - separate from ask-input-container */}
            <div className="status-messages-container">
                {generationStatus && (
                    <div className="generation-status" style={{
                        color: '#3498db',
                        fontSize: '14px',
                        marginBottom: '12px',
                        textAlign: 'center',
                        fontStyle: 'italic'
                    }}>
                        {generationStatus}
                    </div>
                )}
                {error && (
                    <div style={{
                        color: '#e74c3c',
                        fontSize: '14px',
                        marginBottom: '12px',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                )}
                {validationWarnings.length > 0 && !error && (
                    <div style={{
                        color: '#f39c12',
                        fontSize: '13px',
                        marginBottom: '12px',
                        textAlign: 'center',
                        fontStyle: 'italic'
                    }}>
                        {validationWarnings[0]}
                    </div>
                )}
            </div>

            {/* Ask input container - form, character counter, and usage status */}
            <div className="ask-input-container">
                <form onSubmit={handleSubmit} className="ask-input-form">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder={placeholderSubjects[currentPlaceholderIndex]}
                        className={`ask-input-field ${isTransitioning ? 'transitioning' : ''}`}
                        disabled={isGenerating}
                        maxLength={30}
                    />
                    <button
                        type="submit"
                        className="ask-button"
                        disabled={isGenerating}
                    >
                        {isGenerating ? (
                            <>
                                <span className="button-text-desktop">Generating...</span>
                                <span className="button-spinner-mobile">
                                    <CgSpinner className="spinning-icon" />
                                </span>
                            </>
                        ) : (
                            'Ask'
                        )}
                    </button>
                </form>
                {/* Character count indicator when approaching limit */}
                {inputValue.length > 20 && (
                    <div style={{
                        fontSize: '12px',
                        color: inputValue.length > 30 ? '#e74c3c' : '#95a5a6',
                        marginTop: '5px',
                        textAlign: 'right',
                        paddingRight: '10px'
                    }}>
                        {inputValue.length}/30 characters
                    </div>
                )}

                {/* Usage status - stays within ask-input-container */}
                {usageStatus && currentUser?.subscription_type === 'premium' && (
                    <div className="usage-status" style={{
                        color: '#000',
                        fontSize: '14px',
                        marginTop: '8px',
                        textAlign: 'center',
                        fontWeight: '400',
                        padding: '0 20px',
                        maxWidth: '100%',
                        boxSizing: 'border-box'
                    }}>
                        {/* Course remaining counter */}
                        {usageStatus.coursesRemaining} / {usageStatus.monthlyLimit} courses remaining
                        <span style={{
                            display: 'block',
                            fontSize: '12px',
                            marginTop: '4px',
                            color: '#000',
                            opacity: 0.7
                        }}>
                            Resets on {new Date(usageStatus.resetDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AskInput;